import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { LOCALE, ZONA_HORARIA } from '@cmms/shared';

export interface PuntoMedicion {
  /** Identificador de la lectura; hace falta para seleccionarla desde la gráfica. */
  id?: string;
  valor: number;
  /** ISO 8601, tal como lo devuelve la API. */
  fechaHora: string;
}

interface Props {
  puntos: PuntoMedicion[];
  unidad: string | null;
  minimoEsperado?: number | null;
  maximoEsperado?: number | null;
  /** Píxeles, o `'100%'` para llenar el contenedor (que debe tener alto definido). */
  alto?: number | string;
  /** Lectura resaltada (id de `PuntoMedicion`). */
  seleccionadoId?: string | null;
  /** Al pulsar un punto de la gráfica. Sin esta prop, la gráfica no es seleccionable. */
  alSeleccionar?: (id: string) => void;
}

/**
 * Tendencia de las lecturas de un medidor. Cada lectura ocupa el mismo espacio
 * horizontal (orden de registro), así la pendiente entre lecturas se ve tal
 * cual aunque haya días de por medio. La fecha y hora de cada punto aparecen
 * en el eje y en el tooltip. Si hay lecturas dentro de un mismo minuto, el eje
 * muestra también los segundos para distinguirlas.
 */
export function GraficoMediciones({
  puntos,
  unidad,
  minimoEsperado,
  maximoEsperado,
  alto = 240,
  seleccionadoId,
  alSeleccionar,
}: Props): JSX.Element {
  const serie = puntos
    .map((p) => ({ id: p.id, instante: new Date(p.fechaHora).getTime(), fechaHora: p.fechaHora, valor: p.valor }))
    .sort((a, b) => a.instante - b.instante)
    .map((p, indice) => ({ ...p, indice }));
  const seleccionado = serie.find((p) => p.id !== undefined && p.id === seleccionadoId) ?? null;

  const hayMismoMinuto = new Set(serie.map((p) => Math.floor(p.instante / 60000))).size < serie.length;
  const fmtEje = new Intl.DateTimeFormat(LOCALE, {
    timeZone: ZONA_HORARIA,
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...(hayMismoMinuto ? { second: '2-digit' } : {}),
  });
  const fmtDetalle = new Intl.DateTimeFormat(LOCALE, {
    timeZone: ZONA_HORARIA,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return (
    <div style={{ width: '100%', height: alto }}>
      <ResponsiveContainer>
        <LineChart
          data={serie}
          margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
          onClick={(estado: unknown) => {
            const punto = (estado as { activePayload?: { payload?: { id?: string } }[] } | undefined)?.activePayload?.[0]
              ?.payload;
            if (alSeleccionar && punto?.id) alSeleccionar(punto.id);
          }}
          style={alSeleccionar ? { cursor: 'pointer' } : undefined}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--borde-suave)" vertical={false} />
          <XAxis
            dataKey="indice"
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(i: number) => (serie[i] ? fmtEje.format(serie[i].instante).replace(', ', ' ') : '')}
            fontSize={12}
            tick={{ fill: 'var(--texto-medio)' }}
            tickLine={false}
            axisLine={{ stroke: 'var(--borde)' }}
            minTickGap={24}
          />
          <YAxis
            fontSize={12}
            width={56}
            domain={['auto', 'auto']}
            tick={{ fill: 'var(--texto-medio)' }}
            tickLine={false}
            axisLine={false}
            unit={unidad ? ` ${unidad}` : undefined}
          />
          <Tooltip
            cursor={{ stroke: 'var(--borde)', strokeDasharray: '4 4' }}
            formatter={(v) => [unidad ? `${v} ${unidad}` : `${v}`, 'Valor']}
            labelFormatter={(_, payload) => {
              const instante = (payload as unknown as { payload?: { instante?: number } }[] | undefined)?.[0]?.payload?.instante;
              return instante === undefined ? '' : fmtDetalle.format(instante).replace(', ', ' ');
            }}
            contentStyle={{ background: 'var(--panel)', border: '1px solid var(--borde)', borderRadius: 8, fontSize: 12.5 }}
            labelStyle={{ color: 'var(--texto)', fontWeight: 600 }}
            itemStyle={{ color: 'var(--azul)' }}
          />
          {minimoEsperado != null ? (
            <ReferenceLine y={minimoEsperado} stroke="var(--alerta)" strokeDasharray="4 4" label="Mín. esperado" />
          ) : null}
          {maximoEsperado != null ? (
            <ReferenceLine y={maximoEsperado} stroke="var(--falla)" strokeDasharray="4 4" label="Máx. esperado" />
          ) : null}
          {seleccionado ? (
            <>
              <ReferenceLine x={seleccionado.indice} stroke="var(--azul)" strokeDasharray="4 4" />
              <ReferenceDot
                x={seleccionado.indice}
                y={seleccionado.valor}
                r={7}
                fill="var(--panel)"
                stroke="var(--azul)"
                strokeWidth={3}
              />
            </>
          ) : null}
          <Line
            type="monotone"
            dataKey="valor"
            stroke="var(--azul)"
            strokeWidth={2.5}
            dot={{ r: 3.5, fill: 'var(--azul)', strokeWidth: 0 }}
            activeDot={{ r: 6, fill: 'var(--azul)', stroke: 'var(--panel)', strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
